import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import Moment from "moment";
import { toast } from "react-toastify";
import { useCognitoAuth } from "../../aws-amplify-auth";
import { LinkedAccountsApi, UsersApi } from "../../services/api/api";
import LinkRequestDialog from "./LinkRequestDialog";
import UnlinkConfirmDialog from "./UnlinkConfirmDialog";

function displayName(entry) {
  return entry?.email || entry?.username || entry?.userId || "Unknown";
}

function formatDate(value) {
  if (!value) return "—";
  const asString = String(value);
  const momentValue = /^\d+$/.test(asString)
    ? Moment.unix(parseInt(asString, 10))
    : Moment(value);
  return momentValue.isValid() ? momentValue.format("MMM DD, YYYY") : asString;
}

function LinkedAccounts(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { refreshUserProfile } = useCognitoAuth();

  const myUserId = props.user?.sub;
  const accountType = props.user?.app_metadata?.accountType;
  const isUploader = accountType === "uploader";

  const [linkedAccounts, setLinkedAccounts] = useState(
    props.user?.app_metadata?.linkedAccountIds || []
  );
  const [requestOpen, setRequestOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Keep local list in sync if the auth context updates while this page is mounted.
  useEffect(() => {
    const fromContext = props.user?.app_metadata?.linkedAccountIds || [];
    setLinkedAccounts(fromContext);
  }, [props.user?.app_metadata?.linkedAccountIds]);

  const refreshProfile = async () => {
    const token = props.token;
    const t = typeof token === "function" ? await token() : token;
    if (!t) return;
    setLoading(true);
    try {
      const result = await UsersApi.updateProfile(t, { type: "details" });
      if (result.success) {
        setLinkedAccounts(result.data?.linkedAccountIds || []);
      } else {
        toast.error(result.message || "Failed to refresh linked accounts");
      }
    } catch (err) {
      toast.error("Failed to refresh linked accounts: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch freshest list on mount.
  useEffect(() => {
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingIncoming = useMemo(
    () =>
      linkedAccounts.filter(
        (entry) => entry?.status === "pending" && entry?.initiatedBy !== myUserId
      ),
    [linkedAccounts, myUserId]
  );

  const pendingOutgoing = useMemo(
    () =>
      linkedAccounts.filter(
        (entry) => entry?.status === "pending" && entry?.initiatedBy === myUserId
      ),
    [linkedAccounts, myUserId]
  );

  const activeLinks = useMemo(
    () => linkedAccounts.filter((entry) => entry?.status === "active"),
    [linkedAccounts]
  );

  const resolveToken = async () => {
    const token = props.token;
    const t = typeof token === "function" ? await token() : token;
    if (!t || typeof t !== "string") {
      throw new Error("No valid token");
    }
    return t;
  };

  const withLoading = async (fn) => {
    setLoading(true);
    try {
      await fn();
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = (requesterUserId) =>
    withLoading(async () => {
      const t = await resolveToken();
      const result = await LinkedAccountsApi.acceptLink(t, requesterUserId);
      if (result.success) {
        toast.success("Link request accepted");
        await refreshProfile();
        await refreshUserProfile();
      } else {
        toast.error(result.message || "Failed to accept link request");
      }
    });

  // "Decline" maps to the only removal endpoint: /users/v1/link/unlink.
  const handleDecline = (requesterUserId) =>
    withLoading(async () => {
      const t = await resolveToken();
      const result = await LinkedAccountsApi.unlinkAccount(t, requesterUserId);
      if (result.success) {
        toast.success("Link request declined");
        await refreshProfile();
        await refreshUserProfile();
      } else {
        toast.error(result.message || "Failed to decline link request");
      }
    });

  const handleCancel = (targetUserId) =>
    withLoading(async () => {
      const t = await resolveToken();
      const result = await LinkedAccountsApi.unlinkAccount(t, targetUserId);
      if (result.success) {
        toast.success("Outgoing link request canceled");
        await refreshProfile();
        await refreshUserProfile();
      } else {
        toast.error(result.message || "Failed to cancel link request");
      }
    });

  const openUnlink = (userId) => {
    setSelectedUserId(userId);
    setConfirmOpen(true);
  };

  const handleUnlink = () =>
    withLoading(async () => {
      if (!selectedUserId) return;
      const t = await resolveToken();
      const result = await LinkedAccountsApi.unlinkAccount(t, selectedUserId);
      if (result.success) {
        toast.success("Link removed");
        setConfirmOpen(false);
        setSelectedUserId(null);
        await refreshProfile();
        await refreshUserProfile();
      } else {
        toast.error(result.message || "Failed to remove link");
      }
    });

  const handleRequestLink = (targetUserId) =>
    withLoading(async () => {
      const t = await resolveToken();
      const result = await LinkedAccountsApi.requestLink(t, targetUserId);
      if (result.success) {
        toast.success("Link request sent");
        setRequestOpen(false);
        await refreshProfile();
        await refreshUserProfile();
      } else {
        toast.error(result.message || "Failed to send link request");
      }
    });

  const desktopColumns = [
    {
      field: "email",
      headerName: "Account",
      flex: 1,
      renderCell: (params) => displayName(params.row),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      sortable: false,
      renderCell: () => <Chip label="Active" color="success" size="small" />,
    },
    {
      field: "linkedAt",
      headerName: "Linked Date",
      width: 180,
      renderCell: (params) => formatDate(params.row?.linkedAt),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Remove Link">
          <span>
            <IconButton
              aria-label={`Remove link with ${displayName(params.row)}`}
              onClick={() => openUnlink(params.row?.userId)}
              disabled={loading}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 4, px: { xs: 1, sm: 2, md: 3 }, pb: 8 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Linked Accounts
        </Typography>
        {isUploader && (
          <Button
            variant="contained"
            onClick={() => setRequestOpen(true)}
            disabled={loading}
          >
            Link New Account
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Pending Requests
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Incoming
        </Typography>
        {pendingIncoming.length === 0 ? (
          <Typography color="text.secondary">No incoming requests.</Typography>
        ) : (
          <List dense>
            {pendingIncoming.map((entry) => (
              <ListItem
                key={`incoming-${entry.userId}`}
                divider
                secondaryAction={
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAccept(entry.userId)}
                      disabled={loading}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDecline(entry.userId)}
                      disabled={loading}
                    >
                      Decline
                    </Button>
                  </Box>
                }
              >
                <ListItemText
                  primary={displayName(entry)}
                  secondary={`Requested on ${formatDate(entry.linkedAt)}`}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Outgoing
        </Typography>
        {pendingOutgoing.length === 0 ? (
          <Typography color="text.secondary">No outgoing requests.</Typography>
        ) : (
          <List dense>
            {pendingOutgoing.map((entry) => (
              <ListItem
                key={`outgoing-${entry.userId}`}
                divider
                secondaryAction={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleCancel(entry.userId)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                }
              >
                <ListItemText
                  primary={displayName(entry)}
                  secondary={`Requested on ${formatDate(entry.linkedAt)}`}
                /> {/* outgoing */}
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Active Links
        </Typography>

        {activeLinks.length === 0 ? (
          <Typography color="text.secondary">
            No active account links.
          </Typography>
        ) : isMobile ? (
          <List dense>
            {activeLinks.map((entry) => (
              <ListItem
                key={`active-${entry.userId}`}
                divider
                secondaryAction={
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => openUnlink(entry.userId)}
                    disabled={loading}
                  >
                    Remove Link
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {displayName(entry)}
                      </Typography>
                      <Chip label="Active" color="success" size="small" />
                    </Box>
                  }
                  secondary={`Linked on ${formatDate(entry.linkedAt)}`}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ height: 360, width: "100%" }}>
            <DataGrid
              rows={activeLinks}
              columns={desktopColumns}
              getRowId={(row) => row.userId}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              loading={loading}
            />
          </Box>
        )}
      </Paper>

      <LinkRequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        currentUserId={myUserId}
        onSubmit={handleRequestLink}
        loading={loading}
      />

      <UnlinkConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedUserId(null);
        }}
        onConfirm={handleUnlink}
        loading={loading}
      />
    </Box>
  );
}

export default LinkedAccounts;
